import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { productFamilies, products } from '@/mocks/products';
import FilterSidebar from './components/FilterSidebar';
import ProductCard from './components/ProductCard';

type Sort = 'default' | 'az' | 'za';

export default function ProductsPage() {
  const [params, setParams] = useSearchParams();
  const active = params.get('category') && productFamilies.includes(params.get('category') as string) ? (params.get('category') as string) : 'all';
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('default');
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    document.title = 'Product Catalog | NORTHLINE Flow Systems';
  }, []);

  const families = useMemo(() => productFamilies
    .map((name) => ({ name, count: products.filter((p) => p.family === name).length }))
    .filter((f) => f.count > 0), []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = products.filter((p) => (active === 'all' || p.family === active)
      && (!q || `${p.name} ${p.family} ${p.description}`.toLowerCase().includes(q)));
    if (sort === 'az') return [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'za') return [...list].sort((a, b) => b.name.localeCompare(a.name));
    return list;
  }, [active, query, sort]);

  const select = (family: string) => {
    if (family === 'all') setParams({}, { replace: true });
    else setParams({ category: family }, { replace: true });
    setDrawer(false);
  };
  const clear = () => { setQuery(''); setSort('default'); select('all'); };
  const canClear = active !== 'all' || query !== '' || sort !== 'default';

  const sidebar = <FilterSidebar families={families} total={products.length} active={active} onSelect={select} onClear={clear} canClear={canClear} />;

  return (
    <>
      <Navbar />
      <main className="bg-background-50 pt-16 md:pt-20">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-12">
          <header className="pt-12 md:pt-20 pb-10 md:pb-14 border-b border-background-200">
            <nav aria-label="Breadcrumb" className="font-label text-xs tracking-[0.08em] text-foreground-500">
              <Link to="/" className="cursor-pointer hover:text-primary-500 transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-950">Products</span>
            </nav>
            <h1 data-mo="title" data-mo-now className="mt-6 h-section text-foreground-950">PRODUCT CATALOG.</h1>
            <p className="mt-5 t-body text-foreground-600 max-w-xl">Valves, actuators and industrial flow-control components.</p>
          </header>

          <section className="py-10 md:py-14 grid grid-cols-1 lg:grid-cols-[248px_minmax(0,1fr)] gap-x-14">
            {/* Row 1: shared toolbar */}
            <div className="hidden lg:flex items-center h-[50px]">
              <p className="eyebrow !text-[11px] text-foreground-500">Categories</p>
            </div>
            <div className="min-w-0 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 sm:h-[50px]">
              <label className="relative flex-1 min-w-0">
                <span className="sr-only">Search products</span>
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products"
                  className="w-full h-[50px] pl-10 pr-4 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                />
              </label>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setDrawer(!drawer)} aria-expanded={drawer} className="lg:hidden flex-1 sm:flex-none h-[50px] px-4 inline-flex items-center justify-center gap-2 border border-background-300 rounded-lg text-sm text-foreground-950 cursor-pointer whitespace-nowrap">
                  <i className="ri-equalizer-line" /> Filters{active !== 'all' ? ' (1)' : ''}
                </button>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value as Sort)}
                  aria-label="Sort products"
                  className="flex-1 sm:flex-none h-[50px] px-3 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-950 cursor-pointer focus:outline-none focus:border-primary-500"
                >
                  <option value="default">Sort: Category</option>
                  <option value="az">Name A–Z</option>
                  <option value="za">Name Z–A</option>
                </select>
              </div>
            </div>

            {/* Single shared divider */}
            <div className="lg:col-span-2 h-px bg-background-200 mt-6 mb-8" aria-hidden="true" />

            {drawer && (
              <div className="lg:hidden pb-8 mb-8 border-b border-background-200">
                <FilterSidebar families={families} total={products.length} active={active} onSelect={select} onClear={clear} canClear={canClear} showHeading />
              </div>
            )}

            {/* Row 2: content */}
            <aside className="hidden lg:block">
              <div className="sticky top-28">{sidebar}</div>
            </aside>

            <div className="min-w-0">
              <p className="text-sm leading-5 text-foreground-600" aria-live="polite">
                Showing <strong className="text-foreground-950 font-medium">{results.length}</strong> of {products.length} products
                {active !== 'all' && <> in <span className="text-foreground-950">{active}</span></>}
              </p>

              {results.length > 0 ? (
                <div className="mt-8 grid grid-cols-1 min-[480px]:grid-cols-2 xl:grid-cols-3 gap-x-6 md:gap-x-8 gap-y-12">
                  {results.map((p) => <ProductCard key={p.slug} product={p} />)}
                </div>
              ) : (
                <div className="mt-8 py-16 text-center border border-dashed border-background-300 rounded-lg">
                  <p className="text-base text-foreground-950">No products match your search.</p>
                  <p className="mt-2 text-sm text-foreground-600">Try another term or reset the filters.</p>
                  <button type="button" onClick={clear} className="mt-6 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-foreground-50 text-sm px-5 py-2.5 rounded-md cursor-pointer whitespace-nowrap transition-colors">Reset filters</button>
                </div>
              )}

              <div className="mt-16 pt-8 border-t border-background-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-sm text-foreground-600">Can&apos;t find a component? We source beyond this list.</p>
                <Link to="/request-quote" className="inline-flex items-center gap-2 text-sm text-foreground-950 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">Request a quote <i className="ri-arrow-right-line" /></Link>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}